import { useState } from 'react';
import { useActiveLocation } from '@/hooks/useActiveLocation';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { useGetLocationQuery, useUpdateLocationMutation } from '@/store/configApi';
import { Button, Field, Panel, SkeletonRows } from '@/components/ui';

/**
 * Payment and POS configuration, per location.
 *
 * The settings that move real money — the threshold and what happens to an
 * unpaid order — are described in terms of what a customer and a kitchen
 * experience, not in system vocabulary.
 */
export function PaymentSettingsPage() {
  const dispatch = useAppDispatch();
  const { locationId } = useActiveLocation();
  const { data: location, isLoading } = useGetLocationQuery(locationId, { skip: !locationId });
  const [updateLocation, { isLoading: isSaving }] = useUpdateLocationMutation();

  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const value = { ...(location ?? {}), ...draft } as Record<string, string | number | boolean>;

  const set = (key: string, next: unknown) => setDraft((prev) => ({ ...prev, [key]: next }));
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    const result = await updateLocation({ id: locationId, ...draft });

    if ('error' in result) {
      dispatch(pushToast('Could not save your payment settings.', 'error'));
      return;
    }

    dispatch(pushToast('Payment settings saved.', 'success'));
    setDraft({});
  };

  if (isLoading) return <SkeletonRows rows={8} />;

  const posProvider = String(value.posProvider ?? 'NONE');
  const policyMode = String(value.paymentPolicyMode ?? 'NEVER');
  const unpaidAction = String(value.unpaidOrderAction ?? 'MARK_FOR_REVIEW');

  return (
    <div className="space-y-4">
      <h1 className="text-display">Payments</h1>

      <Panel title="Point of sale">
        <div className="space-y-4 px-4 py-4">
          <Field label="POS provider">
            <select
              className="input"
              value={posProvider}
              onChange={(event) => set('posProvider', event.target.value)}
            >
              <option value="CLOVER">Clover</option>
              <option value="SKYTAB">SkyTab</option>
              <option value="GIVEX">Givex</option>
              <option value="NONE">No POS — work orders from this dashboard</option>
            </select>
          </Field>

          {posProvider === 'SKYTAB' || posProvider === 'GIVEX' ? (
            <p className="rounded bg-amber-soft px-3 py-2 text-micro text-amber">
              Sending orders to this POS needs certified partner credentials, which we do not have
              yet. Orders are still taken, recorded and shown here, and payment links still work —
              but they will not reach the till.
            </p>
          ) : null}
        </div>
      </Panel>

      <Panel title="Taking payment">
        <div className="space-y-4 px-4 py-4">
          <Field label="Payment provider">
            <select
              className="input"
              value={String(value.paymentProvider ?? 'PAY_AT_RESTAURANT')}
              onChange={(event) => set('paymentProvider', event.target.value)}
            >
              <option value="SHIFT4">Shift4 — text a secure payment link</option>
              <option value="PAY_AT_RESTAURANT">Pay at the restaurant</option>
              <option value="NONE">None</option>
            </select>
          </Field>

          <Field label="When should a customer pay before pickup?">
            <select
              className="input"
              value={policyMode}
              onChange={(event) => set('paymentPolicyMode', event.target.value)}
            >
              <option value="NEVER">Never — everyone pays at pickup</option>
              <option value="ALWAYS">Always require prepayment</option>
              <option value="REQUIRE_ABOVE_AMOUNT">Only for orders over a certain amount</option>
            </select>
          </Field>

          {policyMode === 'REQUIRE_ABOVE_AMOUNT' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Amount">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input tabular"
                  value={Number(value.paymentThresholdCents ?? 5000) / 100}
                  onChange={(event) =>
                    set('paymentThresholdCents', Math.round(Number(event.target.value) * 100))
                  }
                />
              </Field>

              <Field label="Applies when the total is">
                <select
                  className="input"
                  value={String(value.paymentThresholdOperator ?? 'GREATER_THAN_OR_EQUAL')}
                  onChange={(event) => set('paymentThresholdOperator', event.target.value)}
                >
                  <option value="GREATER_THAN_OR_EQUAL">At or above the amount</option>
                  <option value="GREATER_THAN">Above the amount</option>
                </select>
              </Field>
            </div>
          ) : null}

          <Field label="Payment link expiry">
            <select
              className="input"
              value={String(value.paymentLinkExpiryMinutes ?? 15)}
              onChange={(event) => set('paymentLinkExpiryMinutes', Number(event.target.value))}
            >
              <option value="0">No expiry</option>
              <option value="10">10 minutes</option>
              <option value="15">15 minutes</option>
              <option value="20">20 minutes</option>
              <option value="30">30 minutes</option>
              <option value="45">45 minutes</option>
              <option value="60">60 minutes</option>
            </select>
          </Field>

          <label className="flex items-start gap-2.5">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={Boolean(value.autoSendPaymentLink ?? true)}
              onChange={(event) => set('autoSendPaymentLink', event.target.checked)}
            />
            <span>
              <span className="block text-small text-ink">Text the payment link automatically</span>
              <span className="block text-micro text-slate">
                Turn this off to have the line offer the link and wait for the customer to agree.
              </span>
            </span>
          </label>
        </div>
      </Panel>

      <Panel title="If a customer never pays">
        <div className="space-y-4 px-4 py-4">
          <p className="text-small text-slate">
            Orders reach your kitchen as soon as the customer confirms, so an unpaid payment link
            does not cancel the food. Choose what should happen instead.
          </p>

          <Field label="When the payment link expires">
            <select
              className="input"
              value={unpaidAction}
              onChange={(event) => set('unpaidOrderAction', event.target.value)}
            >
              <option value="MARK_FOR_REVIEW">Flag it for staff to decide</option>
              <option value="KEEP_OPEN">Leave the order alone</option>
              <option value="CANCEL_POS_ORDER_IF_SUPPORTED">
                Cancel the POS order, if the POS allows it
              </option>
            </select>
          </Field>

          {unpaidAction === 'CANCEL_POS_ORDER_IF_SUPPORTED' ? (
            <p className="rounded bg-amber-soft px-3 py-2 text-micro text-amber">
              Clover does not support cancelling an order through its API in this build, so these
              orders will be flagged for review instead. Nothing is ever silently removed from your
              kitchen.
            </p>
          ) : null}
        </div>
      </Panel>

      <div className="flex justify-end">
        <Button onClick={save} loading={isSaving} disabled={!dirty}>
          Save changes
        </Button>
      </div>
    </div>
  );
}
